import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { format, getDaysInMonth, startOfMonth } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getMonthTimecards } from '../../../services/timecardService';
import { useAuth } from '../../../context/AuthContext';
import ExportButton from '../../../components/common/ExportButton';

// Utility functions
const getLocalDateString = (date, includeDay = true) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return includeDay 
        ? localDate.toISOString().split('T')[0]
        : localDate.toISOString().split('T')[0].slice(0, 7);
};

const getLocalMonthStart = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return startOfMonth(localDate);
};


// Sticky column styles
const stickyColumnStyles = {
    username: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: 'background.paper',
        minWidth: '150px',
        borderRight: '1px solid rgba(224, 224, 224, 1)',
        padding: '16px',
        '@media (max-width:900px)': {
            minWidth: '100px',
            padding: '8px',
            fontSize: '0.75rem',
        }
    },
    project: {
        position: 'sticky',
        left: '150px',
        zIndex: 3,
        backgroundColor: 'background.paper',
        minWidth: '200px',
        borderRight: '2px solid rgba(224, 224, 224, 1)', // Thicker border as last sticky column
        padding: '16px',
        '@media (max-width:900px)': {
            left: '100px', // Adjust for smaller username column
            minWidth: '120px',
            padding: '8px',
            fontSize: '0.75rem',
        }
    }
};

const MonthlyViewAll = ({ onBack }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timecards, setTimecards] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const { user, loading: authLoading } = useAuth();

    // Memoize currentMonthStart to prevent unnecessary recalculations
    const currentMonthStart = useMemo(() => getLocalMonthStart(currentDate), [currentDate]);

    // Fetch timecards when date changes
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchTimecardData = async () => {
            try {
                setDataLoading(true);
                const dateString = getLocalDateString(currentMonthStart, false);
                
                console.log('Fetching timecards:', {
                    dateString,
                    month: format(currentMonthStart, 'MMMM yyyy', { locale: enUS }),
                    userRole: user?.role,
                    viewType: 'all'
                });
                console.log('Current user:', user);
                
                // For MonthlyViewAll, we need to explicitly pass viewType='all'
                const data = await getMonthTimecards(dateString, null, 'all');
                console.log('Raw timecard data:', data);
                if (mounted) {
                    // Filter timecards based on user role
                    let filteredTimecards = [...data];
                    
                    // If user is a team lead, filter to only show their team members
                    if (user.role === 'team_lead') {
                        console.log('User is a team lead, filtering timecards');
                        
                        // Get team member IDs
                        let teamMemberIds = [];
                        
                        // Check if user has teamMembers directly
                        if (user.teamMembers && user.teamMembers.length > 0) {
                            console.log('Using teamMembers array directly:', user.teamMembers);
                            teamMemberIds = user.teamMembers.map(id => String(id));
                            // Add the team lead to the list
                            if (!teamMemberIds.includes(String(user.id))) {
                                teamMemberIds.push(String(user.id));
                            }
                        } else if (user.teams && user.teams.length > 0) {
                            // Get only the teams where the user is the team lead
                            const leadTeams = user.teams.filter(team => 
                                String(team.teamLead) === String(user.id)
                            );
                            
                            if (leadTeams.length > 0) {
                                // Get all members from these teams
                                const teamMembers = leadTeams.flatMap(team => team.members || []);
                                
                                // Add the team lead to the list of members if not already included
                                if (!teamMembers.includes(user.id) && !teamMembers.some(id => String(id) === String(user.id))) {
                                    teamMembers.push(user.id);
                                }
                                
                                // Make sure we don't have duplicates
                                teamMemberIds = [...new Set(teamMembers.map(id => String(id)))];
                            } else {
                                // If user is not a team lead for any team, they can only see themselves
                                teamMemberIds = [String(user.id)];
                            }
                        } else {
                            // If user has no teams or teamMembers, they can only see themselves
                            teamMemberIds = [String(user.id)];
                        }
                        
                        console.log('Team member IDs:', teamMemberIds);
                        
                        // Filter timecards to only show those for team members
                        filteredTimecards = filteredTimecards.filter(timecard => 
                            timecard.user && teamMemberIds.includes(String(timecard.user._id))
                        );
                        
                        console.log('Filtered timecards count:', filteredTimecards.length);
                    }
                    
                    // Group timecards by username AND project name, then aggregate hours by day
                    const userProjectGroups = filteredTimecards.reduce((groups, timecard) => {
                        const username = timecard.user?.username || 'Unknown User';
                        const projectName = timecard.project?.name || 'Unknown Project';
                        const groupKey = `${username}|${projectName}`; // Composite key for username + project

                        if (!groups[groupKey]) {
                            groups[groupKey] = {
                                _id: groupKey,
                                user: { username, _id: timecard.user?._id },
                                project: { name: projectName },
                                startDate: timecard.startDate,
                                endDate: timecard.endDate,
                                days: []
                            };
                        }

                        // Add hours from this timecard's days to the group
                        timecard.days?.forEach(day => {
                            if (day && day.date) {
                                const dayString = day.date.split('T')[0];
                                const existingDay = groups[groupKey].days.find(d => d.date.split('T')[0] === dayString);
                                
                                if (existingDay) {
                                    // Add hours to existing day
                                    existingDay.hours = (parseFloat(existingDay.hours) + parseFloat(day.hours || 0)).toString();
                                } else {
                                    // Add new day
                                    groups[groupKey].days.push({
                                        date: day.date,
                                        hours: day.hours || '0'
                                    });
                                }
                            }
                        });
                        
                        return groups;
                    }, {});

                    // Convert groups object to array and sort by username then project name
                    const aggregatedData = Object.values(userProjectGroups)
                        .sort((a, b) => {
                            // First sort by username
                            const usernameCompare = (a.user?.username || '').localeCompare(b.user?.username || '');
                            if (usernameCompare !== 0) return usernameCompare;
                            // Then sort by project name
                            return (a.project?.name || '').localeCompare(b.project?.name || '');
                        });

                    console.log('Timecards received and aggregated:', {
                        originalCount: data.length,
                        filteredCount: filteredTimecards.length,
                        aggregatedCount: aggregatedData.length,
                        month: format(currentMonthStart, 'MMMM yyyy', { locale: enUS })
                    });
                    
                    setTimecards(aggregatedData);
                    setError(null);
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching timecards:', err);
                if (mounted) {
                    setError(err.message);
                    setTimecards([]);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        // Only fetch if we have a user and they are admin/team lead/accountant
        if (user && (user.role === 'admin' || user.role === 'team_lead' || user.role === 'accountant')) {
            fetchTimecardData();
        }

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [currentMonthStart, user]);

    const handlePreviousMonth = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() - 1);
            return newDate;
        });
    };

    const handleNextMonth = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() + 1);
            return newDate;
        });
    };


    // Memoize month info calculation
    const monthInfo = useMemo(() => {
        const daysInMonth = getDaysInMonth(currentMonthStart);
        const days = Array.from({ length: daysInMonth }, (_, i) => {
            const date = new Date(currentMonthStart);
            date.setDate(i + 1);
            return {
                date: date.toISOString()
            };
        });
        
        return { days };
    }, [currentMonthStart]);

    const { days } = monthInfo;

    // Show loading state while auth is loading
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error if no user data or insufficient permissions
    if (!user || (user.role !== 'admin' && user.role !== 'team_lead' && user.role !== 'accountant')) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Alert severity="error">
                    {!user ? 'Authentication required' : 'Insufficient permissions'}
                </Alert>
            </Box>
        );
    }

    console.log('Rendering month:', {
        month: format(currentMonthStart, 'MMMM yyyy', { locale: enUS }),
        userRole: user.role
    });

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                    Monthly View - All Users
                </Typography>
            </Box>

            {/* Navigation Bar */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:600px)': {
                        flexDirection: 'row', // Keep on same line for mobile
                        gap: 0.5,
                        alignItems: 'center',
                        px: 0.5, // Add minimal padding inside the flex container
                        py: 1,
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        '@media (max-width:600px)': {
                            flex: 1,
                            gap: 0.5,
                            marginLeft: '-8px', // Move the entire month navigation widget to the left
                        }
                    }}>
                        <IconButton 
                            onClick={handlePreviousMonth}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        <Typography sx={{
                            '@media (max-width:600px)': {
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }
                        }}>
                            {format(currentMonthStart, 'MMMM yyyy', { locale: enUS })}
                        </Typography>
                        <IconButton 
                            onClick={handleNextMonth}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Box>
                    <ExportButton
                        viewType="monthly"
                        timecards={timecards}
                        isAllUsers={true}
                        date={currentMonthStart}
                        mobileIconOnly={true}
                    />
                </Box>
            </Paper>

            {/* Timecard Table */}
            <TableContainer 
                component={Paper}
                sx={{
                    overflow: 'auto',
                    '& .MuiTable-root': {
                        minWidth: days.length * 100 + 350, // Account for sticky columns (username + project) + days
                        borderCollapse: 'separate',
                        borderSpacing: 0
                    }
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell sx={stickyColumnStyles.username}>
                                Username
                            </TableCell>
                            <TableCell sx={stickyColumnStyles.project}>
                                Project
                            </TableCell>
                            {days.map((day, index) => {
                                const date = new Date(day.date);
                                return (
                                    <TableCell 
                                        key={index} 
                                        align="center"
                                        sx={{
                                            width: `${100 / days.length}%`,
                                            minWidth: '100px',
                                            padding: '16px',
                                            whiteSpace: 'nowrap',
                                            boxSizing: 'border-box',
                                            '@media (max-width:900px)': {
                                                minWidth: '60px',
                                                padding: '8px',
                                                fontSize: '0.7rem',
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'block',
                                            '@media (max-width:900px)': {
                                                display: 'none',
                                            }
                                        }}>
                                            {format(date, 'EEE', { locale: enUS })}<br />
                                            {format(date, 'MMM d', { locale: enUS })}
                                        </Box>
                                        <Box sx={{
                                            display: 'none',
                                            '@media (max-width:900px)': {
                                                display: 'block',
                                            }
                                        }}>
                                            {format(date, 'M/d', { locale: enUS })}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                            <TableCell 
                                align="center"
                                sx={{
                                    minWidth: '100px',
                                    padding: '16px',
                                    '@media (max-width:900px)': {
                                        minWidth: '60px',
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}
                            >
                                Total
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {timecards.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={days.length + 3} align="center">
                                    No timecard data found for this month
                                </TableCell>
                            </TableRow>
                        ) : (
                            timecards.map((timecard) => {
                                // Add detailed logging and more flexible checks
                                if (!timecard) {
                                    console.warn('Timecard is null or undefined');
                                    return null;
                                }
                                
                                console.log('Processing timecard:', timecard);
                                
                                // More granular checks
                                if (!timecard.user) {
                                    console.warn('Timecard missing user:', timecard);
                                    return null;
                                }
                                if (!timecard.project) {
                                    console.warn('Timecard missing project:', timecard);
                                    return null;
                                }
                                if (!Array.isArray(timecard.days)) {
                                    console.warn('Timecard days is not an array:', timecard);
                                    return null;
                                }

                                console.log('Processing timecard:', {
                                    id: timecard._id,
                                    user: timecard.user.username,
                                    project: timecard.project.name,
                                    daysCount: timecard.days.length,
                                    firstDay: timecard.days[0]?.date,
                                    lastDay: timecard.days[timecard.days.length - 1]?.date
                                });

                                return (
                                    <TableRow key={timecard._id}>
                                        <TableCell sx={stickyColumnStyles.username}>
                                            {timecard.user.username || 'Unknown User'}
                                        </TableCell>
                                        <TableCell sx={stickyColumnStyles.project}>
                                            {timecard.project.name || 'Unknown Project'}
                                        </TableCell>
                                        {days.map((day, index) => {
                                            // Find the timecard day that matches our current day
                                            const dayString = day.date.split('T')[0];
                                            const timecardDay = timecard.days?.find(d => 
                                                d.date.split('T')[0] === dayString
                                            );
                                            
                                            return (
                                                <TableCell 
                                                    key={index} 
                                                    align="center"
                                                    sx={{
                                                        minWidth: '100px',
                                                        padding: '16px',
                                                        '@media (max-width:900px)': {
                                                            minWidth: '60px',
                                                            padding: '8px',
                                                            fontSize: '0.75rem',
                                                        }
                                                    }}
                                                >
                                                    {timecardDay?.hours ? parseFloat(timecardDay.hours).toFixed(2) : '0.00'}
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell 
                                            align="center"
                                            sx={{
                                                minWidth: '100px',
                                                padding: '16px',
                                                '@media (max-width:900px)': {
                                                    minWidth: '60px',
                                                    padding: '8px',
                                                    fontSize: '0.75rem',
                                                }
                                            }}
                                        >
                                            {Array.isArray(timecard.days) ? 
                                                timecard.days.reduce((sum, day) => sum + (day?.hours ? parseFloat(day.hours) : 0), 0).toFixed(2) :
                                                '0.00'
                                            }
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {dataLoading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default MonthlyViewAll;
